import { User } from "../models/User";
import { AuthResult } from "../models/AuthResult";

class AuthService {
  constructor() {
    this.baseUrl = "https://pasaportehaciaelempleo.islacristina.org/api";
    this.token = null;
    this.currentUser = null;
    this.isAuthenticated = false;
    this.tokenRefreshTimer = null;
    this.tokenPayload = null; // Store decoded JWT payload
  }

  // Decode JWT token without verification (for client-side inspection)
  decodeJWT(token) {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error("Invalid JWT format");
      }

      const payload = parts[1];
      // Add padding if needed
      const paddedPayload = payload + "=".repeat((4 - (payload.length % 4)) % 4);
      const decoded = atob(paddedPayload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error("Error decoding JWT:", error);
      return null;
    }
  }

  // Check if token is expired
  isTokenExpired(payload = null) {
    const tokenPayload = payload || this.tokenPayload;
    if (!tokenPayload || !tokenPayload.exp) {
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return currentTime >= tokenPayload.exp;
  }

  // Get user role from token
  getUserRole() {
    if (!this.tokenPayload || !this.tokenPayload.claims) {
      return null;
    }
    return this.tokenPayload.claims.role;
  }

  // Get user ID from token
  getUserId() {
    if (!this.tokenPayload) {
      return null;
    }
    return this.tokenPayload.uid;
  }

  // Validate token structure and expiration
  validateTokenStructure(token) {
    const payload = this.decodeJWT(token);
    if (!payload) {
      return { isValid: false, error: "Invalid token format" };
    }

    // In development, be more lenient with token validation
    if (process.env.NODE_ENV === "development") {
      // Just check if we have basic structure
      if (payload.exp && payload.uid) {
        // Check if token is expired
        if (this.isTokenExpired(payload)) {
          return { isValid: false, error: "Token has expired" };
        }
        return { isValid: true, payload };
      }
    }

    // Production validation - strict
    // Check required fields
    const requiredFields = ["aud", "iat", "exp", "iss", "sub", "uid"];
    for (const field of requiredFields) {
      if (!payload[field]) {
        return { isValid: false, error: `Missing required field: ${field}` };
      }
    }

    // Check if token is expired
    if (this.isTokenExpired(payload)) {
      return { isValid: false, error: "Token has expired" };
    }

    // Check issuer
    if (!payload.iss.includes("firebase-adminsdk")) {
      return { isValid: false, error: "Invalid token issuer" };
    }

    return { isValid: true, payload };
  }

  // Set token and decode its payload
  setToken(token) {
    const validation = this.validateTokenStructure(token);
    if (!validation.isValid) {
      throw new Error(`Token validation failed: ${validation.error}`);
    }

    this.token = token;
    this.tokenPayload = validation.payload;
    return validation.payload;
  }

  // Initialize the service (call this on app startup)
  async initialize() {
    try {
      await this.loadTokenFromStorage();

      if (this.token) {
        // Validate token structure first
        const validation = this.validateTokenStructure(this.token);
        if (!validation.isValid) {
          console.warn("Stored token is invalid:", validation.error);
          await this.logout();
          return;
        }

        this.tokenPayload = validation.payload;

        // Then validate with server
        // In development, skip server validation if needed
        if (process.env.NODE_ENV === "development") {
          this.startTokenRefreshTimer();
        } else {
          const isValid = await this.validateTokenWithServer();
          if (isValid) {
            this.startTokenRefreshTimer();
          } else {
            await this.logout();
          }
        }
      }
    } catch (error) {
      console.error("Error initializing auth service:", error);
      await this.logout();
    }
  }

  // Login method
  async login(email, password) {
    try {
      // Real API call (use when API is ready)
      const response = await fetch(`${this.baseUrl}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          password: password,
        }),
      });

      const data = await response.json();

      if (response.ok && data.isOk) {
        // Process and validate the JWT token
        try {
          this.setToken(data.token);
          this.currentUser = User.fromJson(data.user);
          this.isAuthenticated = true;

          await this.saveTokenToStorage();
          this.startTokenRefreshTimer();

          return new AuthResult({
            isSuccess: true,
            message: data.message,
            user: this.currentUser,
            token: this.token,
          });
        } catch (tokenError) {
          return new AuthResult({
            isSuccess: false,
            error: `Token validation failed: ${tokenError.message}`,
            errorCode: "INVALID_TOKEN",
          });
        }
      } else {
        return new AuthResult({
          isSuccess: false,
          error: data.error || "Login failed",
          errorCode: data.code,
        });
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return new AuthResult({
          isSuccess: false,
          error: "No hay conexión a internet",
          errorCode: "NETWORK_ERROR",
        });
      }
      return new AuthResult({
        isSuccess: false,
        error: `Error inesperado: ${error.message}`,
        errorCode: "UNKNOWN_ERROR",
      });
    }
  }

  // Register method
  async register(registrationData) {
    try {
      // Real API call (use when API is ready)
      // console.log('registrationData', registrationData);
      const response = await fetch(`${this.baseUrl}/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(registrationData.toJson()),
      });

      const data = await response.json();

      if (response.status === 201 && data.isOk) {
        return new AuthResult({
          isSuccess: true,
          message: data.message,
        });
      } else {
        return new AuthResult({
          isSuccess: false,
          error: data.error || "Registration failed",
          errorCode: data.code,
        });
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return new AuthResult({
          isSuccess: false,
          error: "No hay conexión a internet",
          errorCode: "NETWORK_ERROR",
        });
      }
      return new AuthResult({
        isSuccess: false,
        error: `Error inesperado: ${error.message}`,
        errorCode: "UNKNOWN_ERROR",
      });
    }
  }

  // Change password method
  async changePassword(oldPassword, newPassword) {
    if (!this.token || this.isTokenExpired()) {
      return new AuthResult({
        isSuccess: false,
        error: "No hay sesión activa",
        errorCode: "AUTH_SESSION_INVALID",
      });
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/changePassword`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": this.token,
        },
        body: JSON.stringify({
          oldPassword: oldPassword,
          newPassword: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok && data.isOk) {
        return new AuthResult({
          isSuccess: true,
          message: data.message,
        });
      } else {
        if (data.code === "AUTH_SESSION_EXPIRED") {
          await this.logout();
        }
        return new AuthResult({
          isSuccess: false,
          error: data.error || "Password change failed",
          errorCode: data.code,
        });
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return new AuthResult({
          isSuccess: false,
          error: "No hay conexión a internet",
          errorCode: "NETWORK_ERROR",
        });
      }
      return new AuthResult({
        isSuccess: false,
        error: `Error inesperado: ${error.message}`,
        errorCode: "UNKNOWN_ERROR",
      });
    }
  }

  // Forgot password method
  async forgotPassword(email) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/forgotPassword`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.toLowerCase().trim() }),
      });

      const data = await response.json();

      if (response.ok && data.isOk) {
        return new AuthResult({
          isSuccess: true,
          message: data.message,
        });
      } else {
        return new AuthResult({
          isSuccess: false,
          error: data.error || "Forgot password failed",
          errorCode: data.code,
        });
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return new AuthResult({
          isSuccess: false,
          error: "No hay conexión a internet",
          errorCode: "NETWORK_ERROR",
        });
      }
      return new AuthResult({
        isSuccess: false,
        error: `Error inesperado: ${error.message}`,
        errorCode: "UNKNOWN_ERROR",
      });
    }
  }

  // Upload curriculum method
  async uploadCurriculum(file, userId) {
    if (!this.token || this.isTokenExpired()) {
      return new AuthResult({
        isSuccess: false,
        error: "No hay sesión activa",
        errorCode: "AUTH_SESSION_INVALID",
      });
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${this.baseUrl}/ciudadanos/${userId}/curriculum`, {
        method: "POST",
        headers: {
          "x-auth-token": this.token,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.isOk) {
        return new AuthResult({
          isSuccess: true,
          message: data.message || "Curriculum uploaded successfully",
        });
      } else {
        if (data.code === "AUTH_SESSION_EXPIRED") {
          await this.logout();
        }
        return new AuthResult({
          isSuccess: false,
          error: data.error || "Upload failed",
          errorCode: data.code,
        });
      }
    } catch (error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        return new AuthResult({
          isSuccess: false,
          error: "No hay conexión a internet",
          errorCode: "NETWORK_ERROR",
        });
      }
      return new AuthResult({
        isSuccess: false,
        error: `Error inesperado: ${error.message}`,
        errorCode: "UNKNOWN_ERROR",
      });
    }
  }

  // User verified check
  get userVerified() {
    return this.currentUser !== null && this.isAuthenticated && this.token !== null && !this.isTokenExpired();
  }

  // Logout method
  async logout() {
    this.currentUser = null;
    this.token = null;
    this.tokenPayload = null;
    this.isAuthenticated = false;
    this.stopTokenRefreshTimer();
    await this.clearTokenFromStorage();
  }

  // Refresh token method
  async refreshToken() {
    if (!this.token || this.isTokenExpired()) {
      await this.logout();
      return false;
    }

    try {
      const response = await fetch(`${this.baseUrl}/auth/refreshToken`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": this.token,
        },
      });

      const data = await response.json();

      if (response.ok && data.isOk) {
        try {
          this.setToken(data.token);
          await this.saveTokenToStorage();
          return true;
        } catch (tokenError) {
          console.error("Error processing refreshed token:", tokenError);
          await this.logout();
          return false;
        }
      } else {
        if (data.code === "AUTH_SESSION_EXPIRED") {
          await this.logout();
        }
        return false;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      return false;
    }
  }

  // Private methods
  async saveTokenToStorage() {
    if (this.token) {
      localStorage.setItem("auth_token", this.token);
    }
    if (this.currentUser) {
      localStorage.setItem("current_user", JSON.stringify(this.currentUser.toJson()));
    }
  }

  async loadTokenFromStorage() {
    this.token = localStorage.getItem("auth_token");
    const userJson = localStorage.getItem("current_user");
    if (userJson) {
      try {
        this.currentUser = User.fromJson(JSON.parse(userJson));
        this.isAuthenticated = true;
      } catch (error) {
        console.error("Error loading user from storage:", error);
        await this.clearTokenFromStorage();
      }
    }
  }

  async clearTokenFromStorage() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("current_user");
  }

  // Renamed for clarity - this validates with server
  async validateTokenWithServer() {
    if (!this.token) return false;

    try {
      const response = await fetch(`${this.baseUrl}/auth/validateToken`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": this.token,
        },
      });

      const data = await response.json();
      return response.ok && data.isOk;
    } catch (error) {
      console.error("Error validating token with server:", error);
      // In development, if server is not available, we'll trust the token structure validation
      // In production, this should return false
      return true; // For development - change to false for production
    }
  }

  // Legacy method - keeping for backward compatibility
  async validateToken() {
    return this.validateTokenWithServer();
  }

  startTokenRefreshTimer() {
    this.stopTokenRefreshTimer();

    // Calculate refresh time based on token expiration
    let refreshInterval = 50 * 60 * 1000; // Default 50 minutes

    if (this.tokenPayload && this.tokenPayload.exp) {
      const expirationTime = this.tokenPayload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;

      // Refresh 10 minutes before expiration, or 50 minutes (whichever is shorter)
      refreshInterval = Math.min(timeUntilExpiration - 10 * 60 * 1000, 50 * 60 * 1000);

      // Ensure we don't set a negative or too short interval
      if (refreshInterval < 60000) {
        // Less than 1 minute
        refreshInterval = 60000; // Set to 1 minute
      }
    }

    this.tokenRefreshTimer = setInterval(() => {
      this.refreshToken();
    }, refreshInterval);
  }

  stopTokenRefreshTimer() {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  // Make authenticated HTTP requests
  async makeAuthenticatedRequest({ method, endpoint, body = null, additionalHeaders = {} }) {
    if (!this.token || this.isTokenExpired()) {
      throw new Error("No valid authentication token available");
    }

    const headers = {
      "Content-Type": "application/json",
      "x-auth-token": this.token,
      ...additionalHeaders,
    };

    const config = {
      method: method.toUpperCase(),
      headers: headers,
    };

    if (body && (method.toUpperCase() === "POST" || method.toUpperCase() === "PUT" || method.toUpperCase() === "PATCH")) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, config);

    // Check if token expired during request
    if (response.status === 401) {
      const data = await response.json();
      if (data.code === "AUTH_SESSION_EXPIRED" || data.code === "INVALID_TOKEN") {
        await this.logout();
        throw new Error("Session expired");
      }
    }

    return response;
  }

  // Helper method to check if user has specific role
  hasRole(role) {
    const userRole = this.getUserRole();
    return userRole === role;
  }

  // Helper method to check if user is ciudadano
  isCiudadano() {
    return this.hasRole("ciudadano");
  }

  // Helper method to check if user is empresa
  isEmpresa() {
    return this.hasRole("empresa");
  }

  // Helper method to check if user is admin
  isAdmin() {
    return this.hasRole("admin");
  }
}

// Create singleton instance
const authService = new AuthService();
export default authService;
